<script setup>
import SearchIcon from "assets/svg/search/SearchIcon.vue";
import HamburgerButton from "assets/svg/hamburger/HamburgerButton.vue";
import { useHeaderStore } from "~/stores/headerState.js";
import { useSearchAction } from "~/composable/useSearchAction.js";
import { useRouter, useRoute } from "vue-router";

const router = useRouter();
const route = useRoute();

// 모바일 헤더를 처리하는 전역 상태 관리
const headerStore = useHeaderStore();
const { setSearchTitleType } = headerStore;

// UX상 현재 검색타입을 확인하기 위한 로직
const searchType = Object.freeze({
  brno: "사업자 등록 번호",
  crno: "법인 등록 번호",
  cname: "상호명",
});
const currentSearchType = ref(null);
const updateSearchType = async (type) => {
  currentSearchType.value = type;
  setSearchTitleType(type);
  headerStore.close();
  await router.push("/");
};

watch(() => route.path, () => {
  if (route.path !== "/") {
    currentSearchType.value = "";
  }
});

// 검색 페이지로 이동처리하는 로직
const { handleSubmit } = useSearchAction();
const searchFormPcRef = ref(null);
const searchFormMoRef = ref(null);
const triggerSubmit = (formRef) => {
  if (formRef.value) {
    formRef.value.dispatchEvent(new Event("submit", { cancelable: true }));
  }
};
const triggerPcSubmit = () => (triggerSubmit(searchFormPcRef));
const triggerMoSubmit = () => (triggerSubmit(searchFormMoRef));
</script>

<template>
  <header>
    <div class="container">
      <NuxtLink to="/" class="partners_logo">
        <NuxtImg src="/images/psLogo.png" alt="partners_logo" format="webp" width="116px" height="25px" />
      </NuxtLink>
      <form ref="searchFormPcRef" @submit="handleSubmit">
        <input type="text" placeholder="사업자 등록 번호/법인등록번호/상호명 입력" />
        <SearchIcon class="search_icon" @click="triggerPcSubmit" />
      </form>
      <nav>
        <ul>
          <li :class="{current: currentSearchType === searchType.brno}" @click="updateSearchType(searchType.brno)">
            사업자등록번호 조회
          </li>
          <li :class="{current: currentSearchType === searchType.crno}" @click="updateSearchType(searchType.crno)">
            법인등록번호 조회
          </li>
          <li :class="{current: currentSearchType === searchType.cname}" @click="updateSearchType(searchType.cname)">상호명
            조회
          </li>
        </ul>
      </nav>
      <div class="mo_button_group">
        <SearchIcon class="mo_search_icon" @click="headerStore.toggleMobileSearchEvent" />
        <HamburgerButton class="mo_hamburger_button" @click="headerStore.toggleMobileMenuEvent" />
      </div>
    </div>
    <aside :class="{ mo_menu: true,  active: headerStore.toggleMobileMenu}">
      <ul>
        <li><span @pointerup="updateSearchType(searchType.brno)">사업자등록번호 조회</span></li>
        <li><span @pointerup="updateSearchType(searchType.crno)">법인등록번호 조회</span></li>
        <li><span @pointerup="updateSearchType(searchType.cname)">상호명 조회</span></li>
      </ul>
    </aside>
    <aside :class="{ mo_search: true,  active: headerStore.toggleMobileSearch}">
      <form ref="searchFormMoRef" @submit="handleSubmit">
        <input type="text" placeholder="사업자 등록 번호/법인등록번호/상호명 입력" />
        <SearchIcon class="search_icon" @click="triggerMoSubmit" />
      </form>
    </aside>
  </header>
</template>

<style lang="scss" scoped>
header {
  display: flex;
  flex-direction: column;
  justify-content: center;

  position: fixed;
  z-index: 99999;

  width: 100%;
  height: 80px;
  background-color: #FFF;
  border-bottom: 1px solid #E6E6E6;
  box-sizing: border-box;

  color: #000;
}

.mo_menu {
  display: none;
}

.mo_search {
  display: none;
}

.mo_button_group {
  display: none;
}

.container {
  display: flex;
  align-content: center;

  position: relative;
  z-index: 99998;

  width: 960px;
  height: 33px;
  margin: 0 auto;

  background-color: #FFF;
}

.container > .partners_logo {
  margin: auto 0;
  margin-right: 36px;
  cursor: pointer;
}

.container > form {
  position: relative;
  margin-right: 141px;

  & > .search_icon {
    width: 14px;
    height: 14px;
    position: absolute;
    color: #C8C8C8;
    top: 50%;
    right: 2.5%;
    transform: translate(-50%, -50%);
    cursor: pointer;
  }

  & > input {
    width: 282px;
    height: 34px;
    padding: 8px 32px 8px 16px;
    box-sizing: border-box;
    font-size: 12px;
    font-weight: 500;
    color: #000;
    border: 1px solid #C8C8C8;
    border-radius: 5px;

    &::placeholder {
      color: #C8C8C8;
      opacity: 1; /* Firefox */
    }

    &::-ms-input-placeholder { /* Edge 12 -18 */
      color: #C8C8C8;
    }
  }
}

nav {
  margin: auto 0;
}

nav ul {
  display: flex;
  align-items: center;
  height: 33px;
  list-style: none;
  padding: 0;
  margin: 0;

  & > li {
    display: inline;
    padding: 0;
    margin-right: 32px;
    font-size: 16px;
    font-weight: 600;
    color: #000;
    cursor: pointer;
  }

  & > li:last-child {
    margin-right: 0;
  }
}

.current {
  color: #3289FA;
}

@media (max-width: 960px) {

  .mo_menu.active {
    top: 80px;
  }

  .mo_search.active {
    top: 80px;
  }

  .mo_search {
    display: flex;
    justify-content: center;
    align-items: center;

    position: absolute;
    z-index: 1;
    top: -120px;
    left: 0;
    width: 100%;
    height: 88px;
    background-color: #FFF;
    transition: top 0.5s cubic-bezier(0.075, 0.820, 0.165, 1.000);

    & > form {
      position: relative;
    }

    & > form > .search_icon {
      width: 14px;
      height: 14px;
      position: absolute;
      color: #C8C8C8;
      top: 50%;
      right: 2.5%;
      transform: translate(-50%, -50%);
      cursor: pointer;
    }

    & > form > input {
      width: 327px;
      height: 40px;
      padding: 8px 32px 8px 16px;
      box-sizing: border-box;
      font-size: 12px;
      font-weight: 500;
      color: #000;
      border: 1px solid #C8C8C8;
      border-radius: 4px;

      &::placeholder {
        color: #C8C8C8;
        opacity: 1; /* Firefox */
      }

      &::-ms-input-placeholder { /* Edge 12 -18 */
        color: #C8C8C8;
      }
    }
  }

  .mo_menu {
    display: block;
    position: absolute;
    z-index: 1;
    top: -120px;
    left: 0;
    width: 100%;
    height: 168px;
    background-color: #FFF;
    transition: top 0.5s cubic-bezier(0.075, 0.820, 0.165, 1.000);

    & > ul {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 120px;
      list-style: none;
      margin: 24px;
      padding: 0;
    }

    & > ul > li > span {
      display: inline-block;
      cursor: pointer;
    }
  }

  .mo_button_group {
    display: block;
    margin: auto 24px auto 0;
  }

  .mo_search_icon {
    width: 24px;
    height: 24px;
    margin-right: 24px;
    cursor: pointer;
  }

  .mo_hamburger_button {
    width: 24px;
    height: 24px;
    cursor: pointer;
  }

  .container {
    width: 100%;
    height: 100%;
    min-width: 280px;
    justify-content: space-between;
  }

  .container > .partners_logo {
    margin: auto 0 auto 24px;
  }

  .container > form {
    display: none;
  }

  nav ul {
    display: none;
  }
}
</style>